'use client';

import * as React from 'react';

type SubscriptionStatus = 'active' | 'inactive' | 'pending' | 'processing' | 'unknown';

type SubscriptionStatusBadgeProps = {
  status: SubscriptionStatus;
  className?: string;
};

const statusConfig = {
  active: {
    label: 'Active',
    icon: '✓',
    bgColor: 'bg-green-500/10',
    textColor: 'text-green-600 dark:text-green-400',
    borderColor: 'border-green-500/20',
    description: 'Your subscription is active and working',
  },
  inactive: {
    label: 'No Subscription',
    icon: '○',
    bgColor: 'bg-gray-500/10',
    textColor: 'text-gray-600 dark:text-gray-400',
    borderColor: 'border-gray-500/20',
    description: 'Subscribe to access chat features',
  },
  pending: {
    label: 'Pending',
    icon: '⏳',
    bgColor: 'bg-yellow-500/10',
    textColor: 'text-yellow-600 dark:text-yellow-400',
    borderColor: 'border-yellow-500/20',
    description: 'Complete BAA agreement to activate',
  },
  processing: {
    label: 'Processing',
    icon: '⟳',
    bgColor: 'bg-blue-500/10',
    textColor: 'text-blue-600 dark:text-blue-400',
    borderColor: 'border-blue-500/20',
    description: 'Subscription is being activated',
  },
  unknown: {
    label: 'Unknown',
    icon: '?',
    bgColor: 'bg-gray-500/10',
    textColor: 'text-gray-600 dark:text-gray-400',
    borderColor: 'border-gray-500/20',
    description: 'Unable to determine subscription status',
  },
} as const;

/**
 * SubscriptionStatusBadge component
 *
 * Displays a colored badge with icon indicating the current subscription status.
 * Provides clear visual feedback about subscription state.
 */
export const SubscriptionStatusBadge: React.FC<SubscriptionStatusBadgeProps> = ({
  status,
  className = '',
}) => {
  const config = statusConfig[status];

  return (
    <div
      className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-full border ${config.bgColor} ${config.textColor} ${config.borderColor} ${className}`}
      title={config.description}
    >
      <span className="text-sm font-medium">{config.icon}</span>
      <span className="text-sm font-medium">{config.label}</span>
    </div>
  );
};

SubscriptionStatusBadge.displayName = 'SubscriptionStatusBadge';
